import React, { useState, useRef, useEffect } from "react";
import MainLayouts from "../../Layout/MainLayouts";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ReactComponent as ArrowLeft } from "bootstrap-icons/icons/arrow-left.svg";
import { useHeadOfficeById, useAllocateStock, useBranchProductUnits } from "../../hooks/useHeadOffice";
import Spinner from "../spinner/Spinner";

const AllocateStock = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const headOfficeId = searchParams.get("id");

  const { data: headOfficeData, isLoading: headOfficeLoading } = useHeadOfficeById(headOfficeId);
  const { data: productUnitsData, isLoading: productsLoading } = useBranchProductUnits(1000);
  const allocateStockMutation = useAllocateStock();

  const [formData, setFormData] = useState({
    head_office_id: headOfficeId,
    product_id: "",
    quantity: "",
    notes: "",
  });

  const [errors, setErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const headOffice = headOfficeData?.data;

  // Extract product units from the API response
  const productUnits = productUnitsData?.data?.data || [];

  // Filter products based on search term
  const filteredProducts = productUnits.filter((unit) => {
    const searchLower = searchTerm.toLowerCase();
    const nameMatch = unit.name?.toLowerCase().includes(searchLower);
    const categoryMatch = unit.product_categories_name?.toLowerCase().includes(searchLower);
    return nameMatch || categoryMatch;
  });

  // Get selected product details
  const selectedProduct = productUnits.find((unit) => unit.id == formData.product_id);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleProductSelect = (productId) => {
    setFormData((prev) => ({ ...prev, product_id: productId }));
    setIsDropdownOpen(false);
    setSearchTerm("");
    if (errors.product_id) {
      setErrors((prev) => ({ ...prev, product_id: "" }));
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setIsDropdownOpen(true);
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.product_id) newErrors.product_id = "Product ID is required";
    if (!formData.quantity) newErrors.quantity = "Quantity is required";
    if (formData.quantity && formData.quantity <= 0) {
      newErrors.quantity = "Quantity must be greater than 0";
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    allocateStockMutation.mutate(
      {
        ...formData,
        quantity: parseInt(formData.quantity, 10),
        product_id: parseInt(formData.product_id, 10),
      },
      {
        onSuccess: () => {
          navigate(`/head-office/inventory?id=${headOfficeId}`);
        },
      }
    );
  };

  return (
    <MainLayouts>
      <div className="p-6">
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate(`/head-office/inventory?id=${headOfficeId}`)}
            className="text-orange1 hover:text-orange-600"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-2xl font-bold">Allocate Stock</h1>
            <p className="text-gray-500">{headOffice?.name || "Loading..."}</p>
          </div>
        </div>

        {headOfficeLoading || productsLoading ? (
          <Spinner loading={headOfficeLoading || productsLoading} />
        ) : !headOffice ? (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-red-500 text-center">Head office not found</div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow p-6">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Product <span className="text-red-500">*</span>
                </label>
                <div className="relative" ref={dropdownRef}>
                  <input
                    type="text"
                    value={
                      isDropdownOpen
                        ? searchTerm
                        : selectedProduct
                        ? `${selectedProduct.product_categories_name} | ${selectedProduct.name}`
                        : ""
                    }
                    onChange={handleSearchChange}
                    onFocus={() => setIsDropdownOpen(true)}
                    className={`w-full border ${
                      errors.product_id ? "border-red-500" : "border-gray-300"
                    } rounded px-4 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-orange1`}
                    placeholder="Search products..."
                  />

                  {isDropdownOpen && (
                    <div
                      className="absolute left-0 right-0 mt-1 bg-white border-2 border-gray-400 rounded-lg shadow-2xl max-h-60 overflow-y-auto z-[9999]"
                      style={{ backgroundColor: '#ffffff' }}
                    >
                      {filteredProducts.length > 0 ? (
                        filteredProducts.map((unit) => (
                          <div
                            key={unit.id}
                            onClick={() => handleProductSelect(unit.id)}
                            style={{ backgroundColor: formData.product_id == unit.id ? '#fed7aa' : '#ffffff' }}
                            className={`px-4 py-3 cursor-pointer border-b border-gray-200 last:border-b-0 ${
                              formData.product_id == unit.id
                                ? "font-semibold text-gray-900"
                                : "text-gray-800 hover:bg-gray-100"
                            }`}
                            onMouseEnter={(e) => {
                              if (formData.product_id != unit.id) {
                                e.currentTarget.style.backgroundColor = '#f3f4f6';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (formData.product_id != unit.id) {
                                e.currentTarget.style.backgroundColor = '#ffffff';
                              }
                            }}
                          >
                            <div className="font-medium">
                              {unit.product_categories_name} | {unit.name}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-3 text-gray-500 text-center" style={{ backgroundColor: '#ffffff' }}>
                          No products found
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {errors.product_id && (
                  <p className="text-red-500 text-sm mt-1">{errors.product_id}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quantity <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleChange}
                  min="1"
                  className={`w-full border ${
                    errors.quantity ? "border-red-500" : "border-gray-300"
                  } rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-orange1`}
                  placeholder="Enter quantity"
                />
                {errors.quantity && <p className="text-red-500 text-sm mt-1">{errors.quantity}</p>}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes <span className="text-gray-500">(Optional)</span>
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows="4"
                  className="w-full border border-gray-300 rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-orange1"
                  placeholder="Enter any additional notes..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-4 mt-6">
              <button
                type="button"
                onClick={() => navigate(`/head-office/inventory?id=${headOfficeId}`)}
                className="px-6 py-2 border border-gray-300 rounded hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={allocateStockMutation.isLoading}
                className="px-6 py-2 bg-orange1 text-white rounded hover:bg-orange-600 transition disabled:opacity-50"
              >
                {allocateStockMutation.isLoading ? "Allocating..." : "Allocate Stock"}
              </button>
            </div>
          </form>
        </div>
        )}
      </div>
    </MainLayouts>
  );
};

export default AllocateStock;
